/**
 * F18 — Customer-facing portal provisioning wizard, packaged as a
 * desktop window body. Registered via `Win95Desktop.tsx` so it opens
 * inside a `WindowFrame` from the Start menu / desktop icon.
 *
 * Same component is rendered by the `/onboard/new` route for the
 * admin-consent return landing (where there's no Win95Desktop chrome).
 *
 * Hard limit (Round 1 ADR-B): NIPE may never call
 * `window.location.href = ms-url`. Admin-consent renders as an
 * `<a href={...}>` that the user clicks.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { useMutation, useQuery } from 'convex/react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { useUser } from '@clerk/react';
import { useWindowContext } from '../desktop/WindowContext';
import {
  connectMicrosoftAccount,
  getActiveAccount,
} from '../../lib/graph/msalClient';
import { parseAdminConsentLanding } from '../../lib/onboard/adminConsentLanding';

import { api } from '../../../convex/_generated/api';
import { TenantStep } from './TenantStep';
import { ConsentStep } from './ConsentStep';
import { FirmSetupStep } from './FirmSetupStep';
import { BYOSeedStep } from './BYOSeedStep';
import { ProgressStep } from './ProgressStep';
import { playPortalWelcome } from './OnboardingCompletedOverlay';
import {
  EMPTY_FORM_STATE,
  type OnboardFormState,
  type OnboardStep,
  type OnboardWizardBridge,
} from './types';
import { testIds } from '../../lib/testIds';
import {
  probeTenantDomain,
  deriveTenantDomainFromEmail,
  deriveTenantDomainFromMicrosoftAccount,
  deriveFirmNameFromDomain,
  deriveSlugFromUser,
  deriveFirmNameFromUser,
} from '../../lib/onboard/tenantDiscovery';
import { HelpTip } from '../ui/HelpTip';
import { ADMIN_CONSENT_INTENT_KEY } from '../../lib/onboard/adminConsentLanding';
import type { Id } from '../../../convex/_generated/dataModel';

// Repancy app reg client id (public — visible in admin-consent URLs).
const APP_CLIENT_ID =
  (import.meta as unknown as { env?: { VITE_MS_APP_CLIENT_ID?: string } })
    .env?.VITE_MS_APP_CLIENT_ID ?? '';

// Mode-aware step ordering. Demo skips Microsoft round-trip;
// advanced runs all six. `intro` is always implicit (not in the
// breadcrumb — user is there before they choose anything). The
// `completed` step renders its own panel after `provisioning`.
const DEMO_STEPS = ['firm', 'byo', 'provisioning', 'completed'] as const;
const ADVANCED_STEPS = [
  'tenant',
  'consent',
  'signin',
  'firm',
  'byo',
  'provisioning',
  'completed',
] as const;

const STEP_LABELS: Record<OnboardStep, string> = {
  intro: 'Intro',
  tenant: 'Tenant',
  consent: 'Consent',
  signin: 'Sign in',
  firm: 'Firm setup',
  byo: 'Sample content',
  provisioning: 'Provisioning',
  completed: 'Done',
};

function WizardBreadcrumb({
  mode,
  step,
}: {
  mode: 'demo' | 'advanced';
  step: OnboardStep;
}) {
  // Intro step shows no breadcrumb — the user is choosing the mode.
  if (step === 'intro') return null;
  const steps: ReadonlyArray<OnboardStep> =
    mode === 'demo' ? DEMO_STEPS : ADVANCED_STEPS;
  const currentIdx = steps.indexOf(step);
  return (
    <ol
      data-testid="onboard-breadcrumb"
      className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs"
      aria-label={`Step ${currentIdx + 1} of ${steps.length}: ${STEP_LABELS[step]}`}
    >
      {steps.map((s, idx) => {
        const isDone = currentIdx > idx;
        const isCurrent = currentIdx === idx;
        return (
          <li
            key={s}
            data-testid={`onboard-breadcrumb-step-${s}`}
            data-state={isCurrent ? 'current' : isDone ? 'done' : 'pending'}
            className={`flex items-center gap-1.5 ${
              isCurrent
                ? 'font-bold text-[#0a246a]'
                : isDone
                  ? 'text-[#2e7d32]'
                  : 'text-gray-700'
            }`}
          >
            <span
              aria-hidden="true"
              className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold ${
                isCurrent
                  ? 'border-[#0a246a] bg-white text-[#0a246a]'
                  : isDone
                    ? 'border-[#2e7d32] bg-[#c8e6c9] text-[#1b5e20]'
                    : 'border-gray-500 bg-white text-gray-700'
              }`}
            >
              {isDone ? '✓' : idx + 1}
            </span>
            <span>{STEP_LABELS[s]}</span>
            {idx < steps.length - 1 && (
              <span aria-hidden="true" className="text-gray-500">
                ›
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export interface OnboardingWizardWindowProps {
  /** Override the initial step (used by route's ?step= query). */
  initialStep?: OnboardStep;
}

export function OnboardingWizardWindow({
  initialStep,
}: OnboardingWizardWindowProps = {}): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { closeWindow } = useWindowContext();
  const { user } = useUser();
  const [step, setStep] = useState<OnboardStep>(initialStep ?? 'intro');
  const [formState, setFormState] = useState<OnboardFormState>(EMPTY_FORM_STATE);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // F18.demo — Mode toggle. 'demo' (default) skips tenant/consent/signin
  // and provisions into our operator-managed sandbox. 'advanced' shows
  // the full Microsoft round-trip we shipped in F18.next for power
  // users who want to provision into their own M365.
  //
  // F18.modeBadge — Persisted in localStorage keyed on the Clerk userId
  // so each user's preferred mode survives reloads on the same device
  // (decision: per-device only, no Convex schema change). Mid-flight
  // switching is BLOCKED — the badge becomes a read-only label once
  // the user is past the intro step.
  const modeStorageKey = user?.id ? `repancy-onboard-mode:${user.id}` : null;
  const [mode, setMode] = useState<'demo' | 'advanced'>(() => {
    if (typeof window === 'undefined' || !modeStorageKey) return 'demo';
    try {
      const stored = window.localStorage.getItem(modeStorageKey);
      return stored === 'advanced' ? 'advanced' : 'demo';
    } catch {
      return 'demo';
    }
  });
  // Hydrate mode after Clerk identity lands (the initial useState reads
  // user?.id which is null on the very first render).
  useEffect(() => {
    if (typeof window === 'undefined' || !modeStorageKey) return;
    try {
      const stored = window.localStorage.getItem(modeStorageKey);
      if (stored === 'advanced' || stored === 'demo') setMode(stored);
    } catch {
      // SSR / private mode; ignore.
    }
  }, [modeStorageKey]);
  // Persist mode whenever it changes (intro-only setter writes localStorage too).
  const persistMode = useCallback(
    (next: 'demo' | 'advanced') => {
      setMode(next);
      if (typeof window === 'undefined' || !modeStorageKey) return;
      try {
        window.localStorage.setItem(modeStorageKey, next);
      } catch {
        // Non-fatal; mode just won't survive reload.
      }
    },
    [modeStorageKey],
  );

  // F18.demo — Read the sandbox tenant from Convex env. Lets us
  // pre-populate the wizard for AI-lawyer beta users without exposing
  // MS_TENANT_ID to the client bundle.
  const sandboxTenant = useQuery(api.onboarding.getDemoSandboxTenant, {});

  const activeDraft = useQuery(api.onboardingDrafts.getActiveDraft, {
    tenantId: formState.tenantId ?? undefined,
  });
  const upsertDraft = useMutation(api.onboardingDrafts.upsertDraft);
  const kickOff = useMutation(api.onboarding.kickOffProvisioning);
  const markCompleted = useMutation(api.onboardingDrafts.markCompleted);
  // F19.4 — write the customer's signed-in Microsoft account onto the
  // draft right after the step-4 MSAL popup resolves. Feeds the F19.5
  // sharing-grant via `portalProvisioningJobs.customerMicrosoftUpn`.
  const persistMicrosoftAccount = useMutation(
    api.onboardingDrafts.persistMicrosoftAccount,
  );
  const markJobFailedFromClient = useMutation(
    api.portalProvisioning.markJobFailedFromClient,
  );
  const templates = useQuery(api.portalProvisioning.listTemplates, {});
  // Live job polling — only fires once a draftId exists. Convex's
  // automatic re-subscribe means the progress UI auto-updates as the
  // Function App POSTs advance/complete to the job row.
  const draftWithJob = useQuery(
    api.onboarding.getDraftWithJob,
    draftId ? { draftId } : 'skip',
  );
  // Branding row lookup — only meaningful after kickOffProvisioning has
  // landed `markJobComplete`, which writes sharePointSiteUrl onto the
  // tenantBranding row. Reactive: surfaces the URLs the moment the
  // Function App posts /admin/portal-wizard-complete.
  const portalBranding = useQuery(
    api.tenantBranding.getBySlug,
    formState.slug ? { clientSlug: formState.slug } : 'skip',
  );
  // Already-have-portal guard: if the user previously provisioned a
  // private workspace (J.6 one-per-user rule), short-circuit the
  // wizard to a "you already have one" panel that links to the
  // existing portal instead of letting kickOff explode with
  // `portal X already exists`.
  const existingPortal = useQuery(api.users.getPrivateWorkspaceState, {});

  // F18.demo — Initial-mount pre-fill, with two strategies:
  //
  //   Demo mode (default): use the sandbox tenant from Convex env + a
  //     Clerk-name-based slug + Clerk-name-based firm. AI-lawyer beta
  //     users never see Microsoft fields. Microsoft steps don't render.
  //
  //   Advanced mode: original F18 v1 path. If the user's Clerk email
  //     belongs to a corporate domain (NOT gmail/etc.), pre-fill
  //     tenantDomain + firmName from it. User confirms via the tenant
  //     probe before consent.
  //
  // Skips if a saved draft exists (resume branch handles that) or if
  // the user has already typed into the fields manually.
  useEffect(() => {
    if (activeDraft === undefined) return;
    if (activeDraft !== null) return;
    if (!user) return;
    // Defensive: user.id should always exist on a hydrated Clerk user,
    // but hot-reload can briefly hand us a partial object. Bail rather
    // than crash deriveSlugFromUser (which requires a non-empty seed).
    if (!user.id) return;
    if (formState.firmName || formState.slug || formState.tenantDomain) return;

    const email = user.primaryEmailAddress?.emailAddress ?? null;
    const firstName = user.firstName ?? null;
    const lastName = user.lastName ?? null;

    if (mode === 'demo') {
      // Demo flow: populate from Clerk identity + sandbox tenant.
      if (sandboxTenant === undefined) return; // wait for Convex env to resolve

      // F18.demo.5 — Prefer the Microsoft external account's email
      // for firm-name guessing if present. That's a stronger signal of
      // the user's actual firm than their personal Clerk email
      // (which is often gmail/yahoo for AI-lawyer beta).
      const msAccount = deriveTenantDomainFromMicrosoftAccount(user.externalAccounts);
      const firmEmail = msAccount?.email ?? email;
      const firm = msAccount
        ? deriveFirmNameFromDomain(msAccount.domain) ||
          deriveFirmNameFromUser({ firstName, lastName, email: firmEmail })
        : deriveFirmNameFromUser({ firstName, lastName, email });
      // Slug uses `unique-names-generator` seeded by the Clerk
      // userId → three-word slugs like `quiet-amber-falcon`.
      // Deterministic (same user → same slug, no re-roll on reload),
      // PII-free (no name/email in the portal URL), no Math.random.
      const slug = deriveSlugFromUser({ seed: user.id });
      const consentGrantedAt = new Date().toISOString();
      setFormState((prev) => ({
        ...prev,
        firmName: firm,
        slug,
        tenantId: sandboxTenant?.tenantId ?? prev.tenantId,
        tenantDisplayName: sandboxTenant?.displayName ?? prev.tenantDisplayName,
        tenantDomain: sandboxTenant?.tenantDomain ?? prev.tenantDomain,
        // Demo path: consent is auto-granted via the operator's app reg.
        consentGrantedAt: prev.consentGrantedAt ?? consentGrantedAt,
      }));
      // CRITICAL: persist the demo seed values immediately so the
      // draft row carries firmName + slug + tenantId. Without this,
      // they live only in React state and the user can advance to
      // `kickOffProvisioning` against an empty row (the pre-D2 bug
      // that left duncan@arewe.ai stuck at "draft incomplete").
      if (sandboxTenant) {
        upsertDraft({
          firmName: firm,
          slug,
          tenantId: sandboxTenant.tenantId,
          tenantDomain: sandboxTenant.tenantDomain,
          tenantDisplayName: sandboxTenant.displayName,
          consentGrantedAt,
        })
          .then((id) => {
            if (!draftId) setDraftId(id);
          })
          .catch((err) => {
            console.error(
              '[onboarding] demo-mode seed upsertDraft failed',
              err,
            );
            const msg = err instanceof Error ? err.message : String(err);
            setSubmitError(
              `Could not initialize demo draft: ${msg}\n\n` +
                `Reload the page; if this persists, contact ops.`,
            );
          });
      }
      return;
    }

    // F18.demo.5 — Advanced mode pre-fill, two sources ranked:
    //
    //   1. Microsoft external account on the Clerk user (strongest
    //      signal — the user literally signed into Repancy via
    //      Microsoft, so Clerk has their org email + tenant on record).
    //      Skips consumer Microsoft accounts (outlook.com etc.).
    //   2. Primary email's domain (fallback — corporate Google
    //      Workspace, Okta, etc.). Skips personal-email providers.
    //
    // Either source feeds tenantDomain + a firm-name guess.
    const msAccount = deriveTenantDomainFromMicrosoftAccount(user.externalAccounts);
    if (msAccount) {
      const guessFirm = deriveFirmNameFromDomain(msAccount.domain);
      setFormState((prev) => ({
        ...prev,
        tenantDomain: msAccount.domain,
        firmName: prev.firmName || guessFirm,
      }));
      return;
    }

    if (!email) return;
    const domain = deriveTenantDomainFromEmail(email);
    if (!domain) return;
    const guessFirm = deriveFirmNameFromDomain(domain);
    setFormState((prev) => ({
      ...prev,
      tenantDomain: domain,
      firmName: prev.firmName || guessFirm,
    }));
  }, [activeDraft, user, mode, sandboxTenant, formState.firmName, formState.slug, formState.tenantDomain, upsertDraft, draftId]);

  // Resume from saved draft on first mount.
  useEffect(() => {
    if (activeDraft === undefined) return;
    if (activeDraft === null) return;
    if (draftId !== null) return;
    setDraftId(String(activeDraft._id));
    setFormState((prev) => ({
      ...prev,
      tenantDomain: activeDraft.tenantDomain ?? '',
      tenantId: activeDraft.tenantId ?? null,
      tenantDisplayName: activeDraft.tenantDisplayName ?? null,
      consentGrantedAt: activeDraft.consentGrantedAt ?? null,
      firmName: activeDraft.firmName ?? '',
      slug: activeDraft.slug ?? '',
      practiceArea: activeDraft.practiceArea ?? '',
      theme: activeDraft.theme ?? '',
      primaryColor: activeDraft.primaryColor ?? prev.primaryColor,
    }));
    if (!initialStep && activeDraft.currentStep) {
      setStep(activeDraft.currentStep as OnboardStep);
    }
  }, [activeDraft, draftId, initialStep]);

  // saveDraft — the ONLY path that writes to Convex. Strips null
  // sentinels (which mean "unset" in the OnboardFormState union but
  // would fail the Convex optional-string validator), logs any error
  // loudly, and rethrows so callers can surface it via setSubmitError.
  // No silent best-effort catches: those produced the F18 demo-mode
  // "draft incomplete" class of bug where the SPA thought it had
  // saved but the row was actually empty.
  //
  // CRITICAL: upsertDraft on the server looks up the existing row by
  // (userId, tenantId). If we send a patch without tenantId after the
  // tenant has been set, the index lookup misses the row and the
  // mutation inserts a NEW empty draft instead of patching the
  // existing one. We auto-attach the known tenantId from formState
  // for any patch that doesn't already carry one.
  const saveDraft = useCallback(
    async (
      patch: Partial<OnboardFormState> & { currentStep?: OnboardStep },
    ) => {
      const args: Record<string, unknown> = { ...patch };
      if (patch.tenantId === null) delete args.tenantId;
      if (patch.tenantDisplayName === null) delete args.tenantDisplayName;
      if (patch.consentGrantedAt === null) delete args.consentGrantedAt;
      if (!('tenantId' in args) && formState.tenantId) {
        args.tenantId = formState.tenantId;
      }
      try {
        const id = await upsertDraft(args);
        if (!draftId) setDraftId(id);
        return id;
      } catch (err) {
        console.error('[onboarding] upsertDraft failed', { patch: args, err });
        throw err;
      }
    },
    [upsertDraft, formState.tenantId, draftId],
  );

  // handleFormChange — pure in-memory update. NO per-keystroke
  // persistence. Convex writes happen at well-defined save boundaries
  // (step transitions, submits, MSAL callbacks) where errors can be
  // surfaced to the user via setSubmitError.
  const handleFormChange = useCallback(
    (patch: Partial<OnboardFormState>) => {
      setFormState((s) => ({ ...s, ...patch }));
    },
    [],
  );

  const goToStep = useCallback(
    async (nextStep: OnboardStep) => {
      setStep(nextStep);
      try {
        await saveDraft({ currentStep: nextStep });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setSubmitError(`Could not save step transition to "${nextStep}": ${msg}`);
      }
      return { ok: true, currentStep: nextStep };
    },
    [saveDraft],
  );

  // F18.next.5 — Post-consent auto-advance.
  // When the user returns from Microsoft's /adminconsent redirect, the
  // URL carries `?admin_consent=True&tenant=<id>`. The wizard's saved
  // draft still has currentStep='consent' from when we stashed the
  // intent. Detect the landing and advance to 'signin' so the user
  // doesn't have to manually click "Continue" on consent again.
  // AdminConsentLandingBanner owns clearing the query string itself.
  const consentLandingHandled = useRef(false);
  useEffect(() => {
    if (consentLandingHandled.current) return;
    if (typeof window === 'undefined') return;
    const landing = parseAdminConsentLanding(window.location.search);
    if (!landing.detected || !landing.success) return;
    if (activeDraft === undefined) return;
    consentLandingHandled.current = true;
    const now = new Date().toISOString();
    setFormState((s) => ({ ...s, consentGrantedAt: now }));
    setStep('signin');
    saveDraft({ consentGrantedAt: now, currentStep: 'signin' }).catch(
      (err) => {
        const msg = err instanceof Error ? err.message : String(err);
        setSubmitError(`Could not save admin-consent landing: ${msg}`);
      },
    );
  }, [activeDraft, saveDraft]);

  const consentUrl = useMemo(() => {
    if (!formState.tenantId || !APP_CLIENT_ID) return '';
    const params = new URLSearchParams({
      client_id: APP_CLIENT_ID,
      redirect_uri: window.location.origin,
      state: 'onboard-wizard',
    });
    return `https://login.microsoftonline.com/${formState.tenantId}/adminconsent?${params.toString()}`;
  }, [formState.tenantId]);

  const handleProbe = useCallback(async (domain: string) => {
    const result = await probeTenantDomain(domain);
    if (result.kind === 'ok') {
      return {
        kind: 'ok' as const,
        tenantId: result.tenantId,
        tenantDisplayName: null,
      };
    }
    return { kind: 'not-found' as const, message: result.message };
  }, []);

  const handleTenantConfirm = useCallback(
    async (args: {
      domain: string;
      tenantId: string;
      tenantDisplayName: string | null;
    }) => {
      setFormState((s) => ({
        ...s,
        tenantDomain: args.domain,
        tenantId: args.tenantId,
        tenantDisplayName: args.tenantDisplayName,
      }));
      setStep('consent');
      try {
        await saveDraft({
          tenantDomain: args.domain,
          tenantId: args.tenantId,
          tenantDisplayName: args.tenantDisplayName,
          currentStep: 'consent',
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setSubmitError(`Could not save tenant confirmation: ${msg}`);
      }
    },
    [saveDraft],
  );

  const handleConsentContinue = useCallback(async () => {
    const now = new Date().toISOString();
    setFormState((s) => ({ ...s, consentGrantedAt: now }));
    setStep('signin');
    try {
      await saveDraft({
        consentGrantedAt: now,
        currentStep: 'signin',
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setSubmitError(`Could not save consent grant: ${msg}`);
    }
  }, [saveDraft]);

  // F18.next — real MSAL sign-in (replaces the v1 mock "Continue" button).
  // Uses the shared msalClient.connectMicrosoftAccount which pops a
  // standard login dialog with GRAPH_SCOPES. On success, advances to
  // the firm-setup step. On cancel/error, leaves the wizard on the
  // signin step with an inline error.
  const [signinBusy, setSigninBusy] = useState(false);
  const [signinError, setSigninError] = useState<string | null>(null);
  const handleSignIn = useCallback(async () => {
    setSigninError(null);
    setSigninBusy(true);
    try {
      await connectMicrosoftAccount();
      const account = getActiveAccount();
      if (!account) {
        setSigninError('Sign-in completed but no active account was returned.');
        setSigninBusy(false);
        return;
      }
      // Verify the signed-in account is in the same tenant we're
      // provisioning into. If not, the user signed into the wrong
      // tenant (common confusion with multi-tenant Microsoft accounts).
      if (
        formState.tenantId &&
        account.tenantId &&
        account.tenantId.toLowerCase() !== formState.tenantId.toLowerCase()
      ) {
        setSigninError(
          `You signed in as ${account.username ?? 'unknown'} in tenant ${account.tenantId.slice(0, 8)}…, but the wizard is targeting tenant ${formState.tenantId.slice(0, 8)}…. Sign out of the wrong account and retry.`,
        );
        setSigninBusy(false);
        return;
      }
      // F19.4 — persist the customer's MS account onto the draft
      // before advancing. UPN feeds F19.5's sharing grant; tenantId +
      // localAccountId let the ACA Job + downstream sharing resolve
      // the same Graph identity without re-prompting MSAL. Required
      // fields (username/tenantId/localAccountId) are all present on
      // any non-anonymous MSAL AccountInfo; bail with a friendly
      // error if MSAL hands back a malformed account (very rare).
      if (
        draftId !== null &&
        typeof account.username === 'string' &&
        account.username.length > 0 &&
        typeof account.tenantId === 'string' &&
        account.tenantId.length > 0 &&
        typeof account.localAccountId === 'string' &&
        account.localAccountId.length > 0
      ) {
        try {
          await persistMicrosoftAccount({
            draftId,
            upn: account.username,
            tenantId: account.tenantId,
            localAccountId: account.localAccountId,
          });
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          setSigninError(`Could not save Microsoft account: ${msg}`);
          setSigninBusy(false);
          return;
        }
      }
      try {
        await saveDraft({ currentStep: 'firm' });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setSigninError(`Could not save step transition: ${msg}`);
        setSigninBusy(false);
        return;
      }
      setStep('firm');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // MSAL popup-cancelled errors are friendly; other errors get raw msg.
      if (msg.includes('user_cancelled') || msg.includes('User cancelled')) {
        setSigninError('Sign-in cancelled. Click "Sign in" again when ready.');
      } else {
        setSigninError(`Sign-in failed: ${msg}`);
      }
    } finally {
      setSigninBusy(false);
    }
  }, [formState.tenantId, draftId, saveDraft, persistMicrosoftAccount]);

  // F18.6d — Advance from "firm" to "byo" (Phase 4 BYO seed-zip step).
  // Flushes the full firm/tenant snapshot before advancing so the draft
  // row carries slug + firmName + tenantId by the time the user reaches
  // the byo step. Without this flush, demo-mode pre-fill values live
  // only in React state (the per-keystroke `handleFormChange` patches
  // only fire on actual user edits), and `kickOffProvisioning` fails
  // with "draft incomplete: slug, firmName, tenantId required".
  const handleFirmSubmit = useCallback(async () => {
    if (!draftId) {
      setSubmitError('Draft not initialized yet; please wait a moment and try again.');
      return;
    }
    setSubmitError(null);
    try {
      await saveDraft({
        ...(formState.tenantId ? { tenantId: formState.tenantId } : {}),
        ...(formState.tenantDomain
          ? { tenantDomain: formState.tenantDomain }
          : {}),
        ...(formState.tenantDisplayName
          ? { tenantDisplayName: formState.tenantDisplayName }
          : {}),
        firmName: formState.firmName,
        slug: formState.slug,
        practiceArea: formState.practiceArea,
        theme: formState.theme,
        primaryColor: formState.primaryColor,
        ...(formState.consentGrantedAt
          ? { consentGrantedAt: formState.consentGrantedAt }
          : {}),
        currentStep: 'byo',
      });
      setStep('byo');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setSubmitError(`Could not save firm setup: ${msg}`);
    }
  }, [
    draftId,
    saveDraft,
    formState.tenantId,
    formState.tenantDomain,
    formState.tenantDisplayName,
    formState.firmName,
    formState.slug,
    formState.practiceArea,
    formState.theme,
    formState.primaryColor,
    formState.consentGrantedAt,
  ]);

  // F-FIRST-RUN-LIVE-DATA Phase 4 — fires from the byo step. If the
  // user attached a zip via BYOSeedStep, the draft already carries
  // byoZipStorageId; kickOffProvisioning copies it onto the job row;
  // kickPortalProvisioner unpacks + merges before ARM /start. If the
  // user skipped, the draft has no zip and the chain runs with the
  // template seed only — identical to pre-Phase-4 behavior.
  const handleByoSubmit = useCallback(async () => {
    if (!draftId) {
      setSubmitError('Draft not initialized yet; please wait a moment and try again.');
      return;
    }
    if (!templates || templates.length === 0) {
      setSubmitError(
        'No portal templates available. An operator must seed at least one template before customers can self-provision.',
      );
      return;
    }
    setSubmitError(null);
    setBusy(true);
    const preferred =
      templates.find((t) => t.practiceArea === formState.practiceArea) ??
      templates[0];
    if (!preferred) {
      setSubmitError('Could not resolve a portal template.');
      setBusy(false);
      return;
    }
    // 2026-05-22 — kickOffProvisioning now schedules the ACA dispatch
    // server-side via ctx.scheduler.runAfter(0, ...). The SPA no longer
    // chains a `useAction(kickPortalProvisioner)` call — that pattern
    // could hang on a dropped WebSocket and leave the row stuck at
    // queued forever (demo-firm-one zombie). Convex guarantees the
    // scheduled action runs at least once even across server restarts.
    let insertedJobId: Id<'portalProvisioningJobs'> | null = null;
    try {
      await saveDraft({ currentStep: 'provisioning' });
      // Retry on slug collision: `kickOff` rejects if the user's
      // slug is already taken by an existing tenantBranding row. The
      // user has provisioned before, or two users seeded by the same
      // dictionary phase collided. Generate a fresh deterministic
      // slug seeded with an attempt counter — same `unique-names-
      // generator` family, no Math.random, no PII in the URL.
      let result;
      let lastErr: unknown = null;
      const baseSeed = user?.id ?? 'anon';
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          result = await kickOff({
            draftId,
            templateId: preferred._id,
          });
          lastErr = null;
          break;
        } catch (err) {
          lastErr = err;
          const msg = err instanceof Error ? err.message : String(err);
          if (!msg.includes('already exists')) {
            // Different error — surface immediately; retrying won't help.
            throw err;
          }
          if (attempt === 2) {
            // Exhausted retries. Surface the original collision msg
            // verbatim plus a "try editing the slug manually" hint.
            throw new Error(
              `${msg}\n\nWe tried 3 different slugs and they're all taken. ` +
                `Reload the wizard and edit the slug field manually to pick a unique one.`,
            );
          }
          // Re-seed with a salt so retries diverge. saveDraft now
          // dropping slug-after-kickoff means we MUST patch the slug
          // BEFORE the next kickOff fires (and our backend still
          // accepts slug changes pre-kickoff, where
          // portalProvisioningJobId is still undefined on the draft).
          const retrySeed = `${baseSeed}#retry-${attempt + 1}`;
          const nextSlug = deriveSlugFromUser({ seed: retrySeed });
          console.warn(
            `[OnboardingWizard] slug "${formState.slug}" taken; retry ${attempt + 1}/2 with "${nextSlug}"`,
          );
          setFormState((s) => ({ ...s, slug: nextSlug }));
          await saveDraft({ slug: nextSlug });
        }
      }
      if (!result || !result.ok) {
        throw lastErr ?? new Error('kickOffProvisioning returned not-ok.');
      }
      insertedJobId = result.jobId as Id<'portalProvisioningJobs'>;
      setStep('provisioning');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // Belt-and-suspenders: if kickOff itself threw AFTER the row
      // was inserted (extremely unlikely now that the schedule is
      // inside the same mutation), still mark the row failed so the
      // UI doesn't sit at "Step 0/4 queued".
      if (insertedJobId !== null) {
        markJobFailedFromClient({
          jobId: insertedJobId,
          errorMessage: `SPA caught: ${msg}`,
        }).catch((markErr) => {
          // Loud-log instead of silently swallowing. If
          // markJobFailedFromClient itself errors, we want to know —
          // otherwise zombie rows accumulate undetected.
          console.error(
            `[OnboardingWizard] markJobFailedFromClient failed for jobId=${insertedJobId}: ${markErr instanceof Error ? markErr.message : String(markErr)}`,
            markErr,
          );
        });
      }
      setSubmitError(
        `Could not start provisioning: ${msg}\n\n` +
          `This is a real backend error — your portal was NOT created. ` +
          `Copy this message and contact ops, or retry once the upstream issue is fixed.`,
      );
      setBusy(false);
    }
  }, [
    draftId,
    templates,
    formState.practiceArea,
    formState.slug,
    user,
    saveDraft,
    kickOff,
    markJobFailedFromClient,
  ]);

  // F18.6f — Once the provisioning job lands in `complete`, mark the
  // draft completed (idempotent) so the wizard advances to the
  // completion screen and won't auto-resume on next visit.
  useEffect(() => {
    if (!draftWithJob?.job) return;
    if (draftWithJob.job.status !== 'complete') return;
    if (!draftId) return;
    if (draftWithJob.draft.completed) return;
    markCompleted({ draftId }).catch((err) => {
      // Loud-log instead of silently swallowing. Without this, a real
      // backend failure here (e.g. auth glitch) would leave the draft
      // un-completed forever and the operator would have no clue.
      const msg = err instanceof Error ? err.message : String(err);
      console.error(
        `[OnboardingWizard] markCompleted failed for draftId=${draftId}: ${msg}`,
        err,
      );
    });
    setStep('completed');
  }, [draftWithJob, draftId, markCompleted]);

  // Surface job failures back into the wizard's error panel.
  useEffect(() => {
    if (!draftWithJob?.job) return;
    if (draftWithJob.job.status !== 'failed') return;
    setBusy(false);
    setSubmitError(
      draftWithJob.job.errorMessage ??
        'Provisioning failed. Check the job row in Convex for details.',
    );
  }, [draftWithJob]);

  // Register the global bridge so NIPE tools can drive the wizard.
  useEffect(() => {
    const bridge: OnboardWizardBridge = {
      currentStep: () => step,
      goToStep,
      setField: async (name, value) => {
        const previousValue = (formState as unknown as Record<string, unknown>)[
          name
        ];
        const patch = { [name]: value } as Partial<OnboardFormState>;
        handleFormChange(patch);
        // NIPE explicitly setting a field IS an intent to save (unlike
        // keystrokes which buffer in React state until a step
        // boundary). Persist immediately so the AI tool call's
        // semantics match user expectations.
        try {
          await saveDraft(patch);
        } catch (err) {
          // OnboardWizardBridge.setField return type permits ok:false
          // but doesn't carry a message field — log loudly here so the
          // failure is visible to the operator at least.
          const msg = err instanceof Error ? err.message : String(err);
          console.error('[onboarding] NIPE setField persist failed', {
            name,
            value,
            err: msg,
          });
          return {
            ok: false,
            previousValue:
              typeof previousValue === 'string' ? previousValue : null,
          };
        }
        return {
          ok: true,
          previousValue:
            typeof previousValue === 'string' ? previousValue : null,
        };
      },
      getState: () => formState,
      submitCurrentStep: async () => {
        if (step === 'tenant') {
          if (formState.tenantId) {
            await goToStep('consent');
            return { kind: 'submitted', nextStep: 'consent' as const };
          }
          return {
            kind: 'error' as const,
            message: 'tenantId not yet probed',
          };
        }
        if (step === 'consent' && formState.consentGrantedAt) {
          await goToStep('firm');
          return { kind: 'submitted', nextStep: 'firm' as const };
        }
        if (step === 'firm') {
          await handleFirmSubmit();
          return { kind: 'submitted', nextStep: 'byo' as const };
        }
        if (step === 'byo') {
          await handleByoSubmit();
          return { kind: 'submitted', nextStep: 'provisioning' as const };
        }
        return {
          kind: 'error' as const,
          message: `cannot auto-submit step ${step}`,
        };
      },
    };
    window.__repancyOnboardWizard = bridge;
    return () => {
      delete window.__repancyOnboardWizard;
    };
  }, [step, formState, goToStep, handleFormChange, handleFirmSubmit, handleByoSubmit, saveDraft]);

  // Stash the wizard intent so the AdminConsentLandingBanner in
  // __root.tsx can route the user back here after Microsoft completes
  // the consent flow. Shape MUST match `isAdminConsentIntent` in
  // adminConsentLanding.ts: { origin, returnTo, startedAt, knownSlugs? }.
  useEffect(() => {
    if (step !== 'consent') return;
    try {
      window.sessionStorage.setItem(
        ADMIN_CONSENT_INTENT_KEY,
        JSON.stringify({
          origin: 'onboard-wizard',
          returnTo: '/onboard/new',
          startedAt: new Date().toISOString(),
          ...(formState.slug ? { knownSlugs: [formState.slug] } : {}),
        }),
      );
    } catch {
      // SSR / private mode; non-fatal.
    }
  }, [step, formState.slug]);

  return (
    <div
      data-testid={testIds.onboard.routeRoot}
      className="flex h-full flex-col gap-4 overflow-auto bg-[#ece9d8] p-4 font-sans text-black"
    >
      <header
        data-testid={testIds.onboard.stepHeader}
        className="border-b border-[#404040] pb-2"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <h1 className="text-lg font-bold">
              Set up Repancy on your SharePoint
            </h1>
            <WizardBreadcrumb mode={mode} step={step} />
            <p className="mt-0.5 text-xs">
              Step: <strong>{step}</strong>
            </p>
          </div>
          {/*
            F18.modeBadge — persistent mode indicator. Clickable on the
            intro step (lets the user swap modes before they invest in
            any draft fields). Read-only label once past intro (per
            decision: block mid-flight switching to keep the draft
            state consistent).
          */}
          {step === 'intro' ? (
            <button
              type="button"
              data-testid="onboard-mode-badge"
              onClick={() => persistMode(mode === 'demo' ? 'advanced' : 'demo')}
              title={
                mode === 'demo'
                  ? 'Currently provisioning into the Repancy demo sandbox (no Microsoft sign-in). Click to switch to your own M365 tenant.'
                  : 'Currently provisioning into your own M365 tenant (Microsoft sign-in required). Click to switch back to the demo sandbox.'
              }
              className={`shrink-0 cursor-pointer rounded-sm border px-2 py-1 text-xs font-semibold uppercase tracking-wide ${
                mode === 'demo'
                  ? 'border-[#2e7d32] bg-[#e8f5e9] text-[#2e7d32] hover:bg-[#c8e6c9]'
                  : 'border-[#1565c0] bg-[#e3f2fd] text-[#1565c0] hover:bg-[#bbdefb]'
              }`}
            >
              {mode === 'demo' ? '🏖 Demo sandbox' : '🏢 Your M365 tenant'}
            </button>
          ) : (
            <span
              data-testid="onboard-mode-badge"
              title={
                mode === 'demo'
                  ? 'Provisioning into the Repancy demo sandbox. Mode is locked once you leave the intro step.'
                  : 'Provisioning into your own M365 tenant. Mode is locked once you leave the intro step.'
              }
              className={`shrink-0 rounded-sm border px-2 py-1 text-xs font-semibold uppercase tracking-wide ${
                mode === 'demo'
                  ? 'border-[#2e7d32] bg-[#e8f5e9] text-[#2e7d32]'
                  : 'border-[#1565c0] bg-[#e3f2fd] text-[#1565c0]'
              }`}
            >
              {mode === 'demo' ? '🏖 Demo sandbox' : '🏢 Your M365 tenant'}
            </span>
          )}
        </div>
      </header>

      {step === 'intro' &&
        existingPortal?.hasPrivate === true &&
        existingPortal.status === 'complete' &&
        (() => {
          // Detect whether the user is already viewing the portal that
          // owns this account. The wizard mounts both as a standalone
          // route (/onboard/new) and as a desktop window inside the
          // portal itself (/c/<slug>/desktop). If the path matches,
          // "Open my portal" would be a no-op — show different copy +
          // a Close button instead.
          const portalPathPrefix = `/c/${existingPortal.slug}`;
          const onPortal = location.pathname.startsWith(portalPathPrefix);
          return (
            <section
              data-testid="onboard-already-have-portal"
              data-on-portal={onPortal ? 'true' : 'false'}
              className="flex flex-col gap-3 border border-[#0a6] bg-[#efe] p-3"
            >
              <h2 className="text-base font-bold">
                {onPortal
                  ? "You're already in your portal."
                  : 'You already have a portal.'}
              </h2>
              <p className="text-sm">
                {onPortal ? (
                  <>
                    This window is the onboarding wizard — you don't
                    need it. Your portal{' '}
                    <code className="bg-white px-1">
                      /c/{existingPortal.slug}
                    </code>{' '}
                    is what's already showing behind it. Close this
                    window to use it.
                  </>
                ) : (
                  <>
                    Your existing portal{' '}
                    <code className="bg-white px-1">
                      /c/{existingPortal.slug}
                    </code>{' '}
                    is live. One portal per account today — open it
                    instead of creating a new one.
                  </>
                )}
              </p>
              <div className="flex flex-wrap gap-2">
                {onPortal ? (
                  <button
                    type="button"
                    data-testid="onboard-already-have-close"
                    onClick={() => closeWindow('/onboard/new')}
                    className="border border-[#404040] bg-[#d0d0d0] px-4 py-1 text-sm font-bold text-black cursor-pointer hover:bg-[#e0e0e0]"
                  >
                    Close this window
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="onboard-already-have-open"
                    // 2026-05-28 — close-then-navigate, see the other
                    // "Open my portal →" button below for the
                    // rationale (the deferred-nav-inside-attention
                    // path silently failed in prod).
                    onClick={() => {
                      closeWindow('/onboard/new');
                      void navigate({
                        to: '/c/$clientSlug/desktop',
                        params: { clientSlug: existingPortal.slug },
                      });
                      void playPortalWelcome();
                    }}
                    className="border border-[#404040] bg-[#d0d0d0] px-4 py-1 text-sm font-bold text-black cursor-pointer hover:bg-[#e0e0e0]"
                  >
                    Open my portal
                  </button>
                )}
              </div>
            </section>
          );
        })()}

      {step === 'intro' &&
        mode === 'demo' &&
        !(
          existingPortal?.hasPrivate === true &&
          existingPortal.status === 'complete'
        ) && (
        <section
          data-testid={testIds.onboard.introStep}
          className="flex flex-col gap-3 p-2"
        >
          <h2 className="text-base font-bold">Provision your sandbox portal</h2>
          <p className="text-sm">
            We'll create a real SharePoint site for you in Repancy's
            sandbox M365 tenant (<code>areweai.sharepoint.com</code>),
            seed it with sample matter documents, and wire it into the
            desktop — Document Library, Prompt Lab, Skill Library, and
            Specialization Lab all bind to the new portal.{' '}
            <strong>No Microsoft sign-in required.</strong>
          </p>
          <p className="text-sm text-gray-800">
            After provisioning, you can{' '}
            <strong>bring in your own SharePoint sites</strong> via
            Settings &rarr; Connect SharePoint (Microsoft's
            <em> Sites.Selected</em> per-site grant), drag in local
            folders, or import an Obsidian vault from the desktop.
          </p>
          <HelpTip k="onboard.startSetup" wrap>
            <button
              type="button"
              data-testid="onboard-intro-start"
              data-xp-default
              // Block until the demo-seed upsert has returned an id.
              // Otherwise the click races with the pre-fill insert
              // and `saveDraft({currentStep:'firm'})` lands without a
              // tenantId before the seed row exists → server inserts
              // a 2nd row missing firmName/slug.
              disabled={draftId === null}
              onClick={() => void goToStep('firm')}
              // XP "default-button" affordance: cream gradient with
              // thicker navy border. Tailwind palette + arbitrary hex
              // backgrounds get clobbered by xp.css's `button` base
              // rule, producing white-on-grey "ghost" buttons.
              className="self-start px-4 py-1 text-sm font-bold text-black cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {draftId === null ? 'Loading…' : 'Start setup'}
            </button>
          </HelpTip>
          <HelpTip k="onboard.advancedMode" wrap>
            <button
              type="button"
              data-testid="onboard-intro-advanced"
              onClick={() => {
                persistMode('advanced');
                void goToStep('tenant');
              }}
              className="self-start border-0 bg-transparent px-0 py-0 text-xs text-blue-700 underline cursor-pointer hover:text-blue-900"
            >
              Advanced: I have my own SharePoint / M365 tenant →
            </button>
          </HelpTip>
        </section>
      )}

      {step === 'intro' && mode === 'advanced' && (
        <section
          data-testid={testIds.onboard.introStep}
          className="flex flex-col gap-3 p-2"
        >
          <h2 className="text-base font-bold">Set up Repancy on your tenant</h2>
          <p className="text-sm">
            You're in advanced mode. We'll walk you through Microsoft
            tenant detection, admin consent, sign-in, and then your
            firm setup. NIPE can suggest values along the way.
          </p>
          <div className="border border-[#d4ba3f] bg-[#fff3cd] p-2 text-xs text-[#5b4500]">
            <strong>Heads up:</strong> the current build still
            provisions the SharePoint site into Repancy's sandbox
            tenant (<code>areweai</code>) regardless of which tenant
            you detect here. The tenant-detection, consent, and
            sign-in steps below capture customer identity for the
            audit trail and future sharing-grant work, but they don't
            redirect the provisioner. Switch back to the demo flow if
            you just want a working portal in ~90s.
            <br />
            <br />
            <strong>Why:</strong> Repancy isn't yet a Microsoft
            Verified Publisher, which Microsoft requires before
            cross-tenant provisioning will work.
          </div>
          <button
            type="button"
            data-testid="onboard-intro-start"
            onClick={() => void goToStep('tenant')}
            className="self-start border border-[#404040] bg-[#d0d0d0] px-4 py-1 text-sm font-bold text-black cursor-pointer hover:bg-[#e0e0e0]"
          >
            Start
          </button>
          <button
            type="button"
            data-testid="onboard-intro-back-to-demo"
            onClick={() => {
              persistMode('demo');
            }}
            className="self-start border-0 bg-transparent px-0 py-0 text-xs text-blue-700 underline cursor-pointer hover:text-blue-900"
          >
            ← Back to the demo sandbox flow
          </button>
        </section>
      )}

      {step === 'tenant' && (
        <TenantStep
          initialDomain={formState.tenantDomain}
          onProbe={handleProbe}
          onConfirm={handleTenantConfirm}
        />
      )}

      {step === 'consent' && (
        <ConsentStep
          tenantId={formState.tenantId ?? ''}
          tenantDomain={formState.tenantDomain}
          tenantDisplayName={formState.tenantDisplayName}
          consentUrl={consentUrl}
          alreadyConsented={!!formState.consentGrantedAt}
          onContinue={handleConsentContinue}
          onRestart={() => void goToStep('tenant')}
        />
      )}

      {step === 'signin' && (
        <section
          data-testid={testIds.onboard.signinStep}
          className="flex flex-col gap-3 p-2"
        >
          <h2 className="text-base font-bold">Sign in to your Microsoft account</h2>
          <p className="text-sm">
            Sign in with a SharePoint admin or Global Admin account in
            {formState.tenantDisplayName ? (
              <>
                {' '}<strong>{formState.tenantDisplayName}</strong>.
              </>
            ) : (
              <> the tenant you just consented in.</>
            )}{' '}
            Repancy will request <code>Files.ReadWrite.All</code> and
            <code> Sites.ReadWrite.All</code> so it can create the new portal site.
          </p>
          <div className="border border-[#d4ba3f] bg-[#fff3cd] p-2 text-xs text-[#5b4500]">
            Sandbox-mode note: the actual provisioner uses Repancy's
            app-only token on <code>areweai</code>. The UPN you sign
            in with here is recorded for the audit log + future
            sharing-grant work, but skipping (or signing in with any
            account) won't block provisioning. Click "Skip for now"
            if you don't have a Microsoft Global Admin handy.
          </div>
          <HelpTip k="onboard.signinMicrosoft" wrap>
            <button
              type="button"
              data-testid="onboard-signin-popup"
              onClick={() => void handleSignIn()}
              disabled={signinBusy}
              className="self-start border border-[#404040] bg-[#d0d0d0] px-4 py-1 text-sm font-bold text-black cursor-pointer hover:bg-[#e0e0e0] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {signinBusy ? 'Signing in…' : 'Sign in with Microsoft'}
            </button>
          </HelpTip>
          {signinError && (
            <p
              data-testid="onboard-signin-error"
              className="border border-[#a00] bg-[#fee] p-3 text-sm text-black"
            >
              {signinError}
            </p>
          )}
          <button
            type="button"
            data-testid="onboard-signin-skip"
            onClick={() => void goToStep('firm')}
            className="self-start border border-[#404040] bg-[#d0d0d0] px-3 py-0.5 text-xs text-black cursor-pointer hover:bg-[#e0e0e0]"
          >
            Skip for now (sandbox mode — provisioner uses areweai app-only token)
          </button>
        </section>
      )}

      {step === 'firm' && (
        <>
          <FirmSetupStep
            state={formState}
            onChange={handleFormChange}
            onSubmit={() => void handleFirmSubmit()}
            busy={busy}
          />
          {submitError && (
            <div
              data-testid="onboard-firm-error"
              className="mx-4 mb-2 border-2 border-[#a00] bg-[#fee] p-3 text-sm text-black"
            >
              <p className="mb-1 font-bold uppercase tracking-wide text-[#700]">
                Backend error — portal NOT created
              </p>
              <pre className="whitespace-pre-wrap break-words font-mono text-[11px] text-black">
                {submitError}
              </pre>
            </div>
          )}
        </>
      )}

      {step === 'byo' && (
        <>
          <BYOSeedStep
            draftId={draftId}
            byoFileName={
              (activeDraft as { byoZipFileName?: string } | null)?.byoZipFileName ?? null
            }
            byoFileSize={
              (activeDraft as { byoZipFileSize?: number } | null)?.byoZipFileSize ?? null
            }
            onSkip={() => void handleByoSubmit()}
            onContinue={() => void handleByoSubmit()}
            busy={busy}
          />
          {submitError && (
            <div
              data-testid="onboard-byo-submit-error"
              className="mx-4 mb-2 border-2 border-[#a00] bg-[#fee] p-3 text-sm text-black"
            >
              <p className="mb-1 font-bold uppercase tracking-wide text-[#700]">
                Backend error — portal NOT created
              </p>
              <pre className="whitespace-pre-wrap break-words font-mono text-[11px] text-black">
                {submitError}
              </pre>
            </div>
          )}
        </>
      )}

      {step === 'provisioning' && (
        <ProgressStep
          status={draftWithJob?.job?.status ?? null}
          stepsCompleted={draftWithJob?.job?.stepsCompleted ?? 0}
          stepsTotal={draftWithJob?.job?.stepsTotal ?? 4}
          lastStepLabel={draftWithJob?.job?.lastStepLabel ?? 'queued'}
          errorMessage={draftWithJob?.job?.errorMessage ?? null}
          portalSlug={formState.slug}
          jobCreatedAt={draftWithJob?.job?.createdAt ?? null}
          sharePointSiteUrl={portalBranding?.sharePointSiteUrl ?? null}
          sharePointShareUrl={portalBranding?.sharePointShareUrl ?? null}
          onEnterPortal={() =>
            // Skip /c/$slug landing — it forces an MSAL sign-in popup
            // the user hasn't been primed for after a 5-min onboarding
            // wizard. Route straight to the desktop where their seeded
            // sample data is already visible without MSAL.
            void navigate({
              to: '/c/$clientSlug/desktop',
              params: { clientSlug: formState.slug },
            })
          }
        />
      )}

      {step === 'completed' && (
        <section
          data-testid={testIds.onboard.completedStep}
          className="flex flex-col gap-4 p-2"
        >
          <h2 className="text-base font-bold">Welcome aboard.</h2>
          <p className="text-sm">
            Your portal is live at{' '}
            <strong>/c/{formState.slug}</strong> and seeded with sample
            legal documents you can start exploring.
          </p>

          <div className="flex flex-col gap-2">
            <button
              type="button"
              data-testid="onboard-completed-enter-portal"
              // 2026-05-28 — match the close-then-navigate pattern the
              // SharePoint + Obsidian buttons below already use. Two
              // changes from the previous shape:
              //   1. Close the wizard window BEFORE navigating so the
              //      destination desktop isn't hidden behind it. Same
              //      reason the other two buttons below close first.
              //   2. Navigate synchronously instead of deferring inside
              //      requestAttention. In prod the deferred-navigate
              //      path silently failed for some users — URL never
              //      updated, nothing happened. The XP welcome overlay
              //      animation is a nice-to-have; the navigation is
              //      the actual user action and must not depend on
              //      the attention queue's state.
              // We still kick the overlay animation so the moment
              // feels animated — it just doesn't gate the nav.
              onClick={() => {
                closeWindow('/onboard/new');
                void navigate({
                  to: '/c/$clientSlug/desktop',
                  params: { clientSlug: formState.slug },
                });
                void playPortalWelcome();
              }}
              className="self-start border border-[#404040] bg-[#d0d0d0] px-4 py-1 text-sm font-bold text-black cursor-pointer hover:bg-[#e0e0e0]"
            >
              Open my portal →
            </button>
          </div>

          <div className="mt-2 border-t border-[#aca899] pt-3">
            <h3 className="text-sm font-bold">
              Bring in your own content
            </h3>
            <p className="mt-1 text-xs text-gray-800">
              Your portal already has sample data. Add your real docs
              using any of these paths:
            </p>
            <div className="mt-2 flex flex-col gap-1.5">
              <button
                type="button"
                data-testid="onboard-completed-connect-sharepoint"
                // Navigate to /desktop directly. The index route at
                // /c/$slug only forwards ?panel= when MSAL is signed
                // in — demo-mode users haven't signed in, so they'd
                // land on the Microsoft-sign-in tile with the query
                // string silently dropped. /desktop has the
                // PanelAutoOpener that actually opens the window.
                //
                // 2026-05-27 — also close THIS wizard window before
                // navigating. Without this, the wizard stays mounted
                // (it's a desktop window itself) and the freshly-
                // opened Settings window mounts behind it, so the
                // user perceives "nothing happened except the
                // wallpaper changed color." closeWindow is keyed by
                // the registered window path, not the route URL.
                onClick={() => {
                  closeWindow('/onboard/new');
                  void navigate({
                    to: '/c/$clientSlug/desktop',
                    params: { clientSlug: formState.slug },
                    search: { panel: 'connect-sharepoint' },
                  });
                }}
                className="self-start border border-[#404040] bg-[#d0d0d0] px-3 py-0.5 text-xs text-black cursor-pointer hover:bg-[#e0e0e0]"
              >
                🌐 Connect my SharePoint sites
              </button>
              <button
                type="button"
                data-testid="onboard-completed-import-obsidian"
                // Same close-then-navigate pattern as the SharePoint
                // button above. Without closeWindow, the obsidian-
                // import window opens BEHIND the wizard and the user
                // sees only the portal wallpaper color change.
                onClick={() => {
                  closeWindow('/onboard/new');
                  void navigate({
                    to: '/c/$clientSlug/desktop',
                    params: { clientSlug: formState.slug },
                    search: { panel: 'obsidian-import' },
                  });
                }}
                className="self-start border border-[#404040] bg-[#d0d0d0] px-3 py-0.5 text-xs text-black cursor-pointer hover:bg-[#e0e0e0]"
              >
                📓 Import an Obsidian vault
              </button>
              <p className="mt-1 text-xs text-gray-700">
                Or drag a folder onto the desktop once you're in — NIPE
                will offer to import it.
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
